import { useEffect, useState } from "react";
import { platformApi, executionApi } from "@/lib/api-client";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Globe, Landmark, Eraser } from "lucide-react";
import { toast } from "sonner";

// Super-Admin only — renders nothing for regular Commanders (backend returns 403).
export default function PlatformControl() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    platformApi.config().then(setCfg).catch(() => setCfg(null));
  }, []);

  if (!cfg) return null;

  const toggle = async (enabled) => {
    const msg = enabled
      ? "ARM PUBLIC ROLLOUT? Every new signup will be auto-routed into Stripe Connect onboarding."
      : "Stand down to personal cockpit? New signups will no longer enter Stripe Connect onboarding.";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await platformApi.setPublicRollout(enabled);
      setCfg((c) => ({ ...c, public_rollout_enabled: r.public_rollout_enabled }));
      toast.success(r.public_rollout_enabled
        ? "PUBLIC ROLLOUT ARMED — Connect onboarding live for new signups"
        : "Public Rollout OFF — personal cockpit mode");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setBusy(false); }
  };

  const runCleanup = async () => {
    if (!window.confirm("Executor will permanently purge all test_*@umbrella.io accounts and their data. Proceed?")) return;
    try {
      const r = await executionApi.systemCleanup();
      toast.success(`Executor cleanup complete — ${r.deleted_users} accounts purged, ${r.users_remaining} users remain`);
    } catch { toast.error("Cleanup failed"); }
  };

  return (
    <div data-testid="platform-control" className="pt-6 border-t border-white/10">
      <div className="uc-chip mb-2"><Globe size={11} className="text-amber-400"/> SOVEREIGN CONTROL · SUPER-ADMIN</div>
      <div className="flex items-center justify-between p-3 border border-white/10 bg-black">
        <div>
          <div className="text-sm text-white/90 font-mono-uc uppercase tracking-wider">Public Rollout</div>
          <p className="text-[10px] text-white/40 mt-0.5 max-w-[230px]">
            {cfg.public_rollout_enabled
              ? "ON — new signups auto-enter Stripe Connect onboarding."
              : "OFF — personal cockpit. Connect logic stays dormant."}
          </p>
        </div>
        <Switch
          data-testid="public-rollout-toggle"
          checked={!!cfg.public_rollout_enabled}
          disabled={busy}
          onCheckedChange={toggle}
        />
      </div>
      <div className="flex items-center gap-2 mt-2 text-[10px] font-mono-uc uppercase tracking-wider text-white/40">
        <Landmark size={11} className={cfg.public_rollout_enabled ? "text-amber-400" : "text-white/30"}/>
        <span data-testid="connect-summary">
          Connect: {cfg.public_rollout_enabled ? "ARMED" : "DORMANT"} · {cfg.connect_accounts_count} account{cfg.connect_accounts_count === 1 ? "" : "s"} · Stripe {cfg.stripe_mode}
        </span>
      </div>
      <div className="mt-4">
        <Button
          data-testid="executor-cleanup-btn"
          onClick={runCleanup}
          variant="ghost"
          className="rounded-none h-10 w-full border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs font-mono-uc uppercase tracking-wider"
        >
          <Eraser size={13} className="mr-2"/> Executor: Purge Test Accounts
        </Button>
        <p className="text-[10px] text-white/40 mt-1">Executor autonomously deletes all test_*@umbrella.io accounts + cascaded data and seals a signed report in the Executed Vault.</p>
      </div>
    </div>
  );
}
